import ScheduleEntry from '../models/ScheduleEntry.ts';
import { mapScheduleEntryFromApi } from '../mappers/scheduleEntryApiMapper';

/**
 * Converts raw schedule data (from API) to ScheduleEntry objects
 * @param {Array} rawItems - Raw schedule items from API or data source
 * @returns {Array} - Array of ScheduleEntry objects
 */
export const convertToScheduleEntries = (rawItems) => {
  if (!Array.isArray(rawItems)) return [];
  return rawItems.map(item => item instanceof ScheduleEntry ? item : new ScheduleEntry(mapScheduleEntryFromApi(item)));
};

/**
 * Converts ScheduleEntry objects back to plain objects for API calls
 * @param {Array} scheduleEntries - Array of ScheduleEntry objects
 * @returns {Array} - Array of plain objects for API
 */
export const convertToPlainObjects = (scheduleEntries) => {
  if (!Array.isArray(scheduleEntries)) return [];
  return scheduleEntries.map(entry => 
    entry instanceof ScheduleEntry ? entry.toObject() : entry
  );
};

/**
 * Groups schedule entries by day
 * @param {Array} scheduleEntries - Array of ScheduleEntry objects
 * @returns {Object} - Object with days as keys and arrays of entries as values
 */
export const groupEntriesByDay = (scheduleEntries) => {
  const entriesByDay = {};
  
  if (!Array.isArray(scheduleEntries)) return entriesByDay;
  
  scheduleEntries.forEach(entry => {
    if (!entry.day) return;
    if (!entriesByDay[entry.day]) {
      entriesByDay[entry.day] = [];
    }
    entriesByDay[entry.day].push(entry);
  });
  
  return entriesByDay;
};

/**
 * Groups schedule entries by room
 * @param {Array} scheduleEntries - Array of ScheduleEntry objects
 * @returns {Object} - Object with rooms as keys and arrays of entries as values
 */
export const groupEntriesByRoom = (scheduleEntries) => {
  const entriesByRoom = {};
  
  if (!Array.isArray(scheduleEntries)) return entriesByRoom;
  
  scheduleEntries.forEach(entry => {
    if (!entry.room) return;
    if (!entriesByRoom[entry.room]) {
      entriesByRoom[entry.room] = [];
    }
    entriesByRoom[entry.room].push(entry);
  });
  
  return entriesByRoom;
};

/**
 * Format time ranges for display (hh:mm-hh:mm)
 * @param {Object} scheduleEntry - ScheduleEntry object or time string from database
 * @returns {string} - Formatted time string without seconds
 */
export const formatTimeRange = (scheduleEntryOrString) => {
  // If it's a ScheduleEntry object
  if (scheduleEntryOrString && typeof scheduleEntryOrString === 'object' && scheduleEntryOrString.timeRangeDisplay) {
    return scheduleEntryOrString.timeRangeDisplay();
  }
  
  // If it's a string (legacy support)
  const timeString = scheduleEntryOrString;
  if (!timeString || typeof timeString !== 'string') return '';
  
  // If it's already in the format "hh:mm-hh:mm"
  if (timeString.includes('-')) {
    const [start, end] = timeString.split('-');
    return `${formatTimeComponent(start.trim())}-${formatTimeComponent(end.trim())}`;
  }
  
  return formatTimeComponent(timeString);
};

/**
 * Helper function to format individual time components
 * @param {string} time - Time string (may contain seconds)
 * @returns {string} - Formatted time without seconds
 */
function formatTimeComponent(time) {
  if (!time) return '';
  
  // If contains seconds (hh:mm:ss)
  if (time.includes(':')) {
    const parts = time.split(':');
    if (parts.length === 3) {
      return `${parts[0]}:${parts[1]}`;
    }
  }
  
  return time;
}

/**
 * Validates a week series string format
 * @param {string} weekSeries - The week series string to validate (e.g., "38,44" or "52-4")
 * @returns {boolean} - Whether the format is valid
 */
export const validateWeekSeries = (weekSeries) => {
  if (!weekSeries || typeof weekSeries !== 'string') return false;

  // Remove all whitespace
  const cleanWeekSeries = weekSeries.replace(/\s/g, '');

  // Split by commas
  const parts = cleanWeekSeries.split(',');

  // Check each part
  return parts.every(part => {
    // Check if it's a single week number
    if (/^\d{1,2}$/.test(part)) {
      const week = parseInt(part, 10);
      return week >= 1 && week <= 53;
    }
    
    // Check if it's a week range (e.g., "52-4")
    if (/^\d{1,2}-\d{1,2}$/.test(part)) {
      const [start, end] = part.split('-').map(n => parseInt(n, 10));
      // Allow wrapping around the year (e.g., "52-4" means weeks 52,53,1,2,3,4)
      if (start >= 1 && start <= 53 && end >= 1 && end <= 53) {
        return true;
      }
    }
    
    return false;
  });
}; 